import React, { useState, useEffect, useMemo } from 'react';
import { verdictLabel } from '../../utils/formatting';
import PropTypes from 'prop-types';
import { daysInStockSeverity } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { VERDICT_STYLE } from './branchOverviewStyles';
import BranchAiValueTable from './BranchAiValueTable';
import {
    aggregateBranches, alignmentLabels, alignmentTone, categoryBreakdown, pctOf, priceAlignment, sellSpeedBreakdown, verdictBreakdown,
} from '../../utils/branchView';

// Days-in-stock colours: past the last aging boundary, past the critical days.
const DAYS_COLORS = { crit: '#b91c1c', warn: '#b45309', ok: '#64748b' };
// Avg Ask / AI colour (utils/branchView alignmentTone).
const ALIGN_COLORS = { crit: '#b91c1c', below: '#1d4ed8', ok: '#15803d' };

export default function BranchCombinedView({
    data, loading, error, navigate, invByBranch, invUnits, inventoryLoading,
}) {
    const [selectedBranches, setSelectedBranches] = useState(() => new Set());

    // Initialize with the first branch when data loads
    useEffect(() => {
        if (data?.branches?.length && selectedBranches.size === 0) {
            setSelectedBranches(new Set([data.branches[0].branch_name]));
        }
    }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const allBranchNames = useMemo(
        () => (data?.branches || []).map(b => b.branch_name),
        [data]
    );

    const isAllSelected = allBranchNames.length > 0 && selectedBranches.size === allBranchNames.length;

    const toggleBranch = (name) => {
        setSelectedBranches(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const selectAll = () => setSelectedBranches(new Set(allBranchNames));
    const clearAll = () => setSelectedBranches(new Set());

    // The dealership's aging buckets and critical days (90 / 180 by default).
    const { aging, gapThresholds } = useDealershipSettings();
    const bands = alignmentLabels(gapThresholds);
    const bucketLabel = (id) => aging.buckets[id - 1].label;
    // Units across all selected branches
    const branchUnits = useMemo(() => {
        if (!invByBranch) return [];
        return [...selectedBranches].flatMap(name => invByBranch[name] || []);
    }, [invByBranch, selectedBranches]);

    // Today's formulas, shared with the v2 page (utils/branchView, D16).
    const categoryData = useMemo(() => categoryBreakdown(branchUnits, aging.criticalDays), [branchUnits, aging.criticalDays]);
    const verdictData = useMemo(() => verdictBreakdown(branchUnits), [branchUnits]);
    const priceRealization = useMemo(() => priceAlignment(branchUnits, gapThresholds), [branchUnits, gapThresholds]);
    const speedData = useMemo(() => sellSpeedBreakdown(branchUnits), [branchUnits]);

    // Aggregated branch stats — single branch passes through, multi aggregates
    const aggregated = useMemo(
        () => aggregateBranches(data?.branches, selectedBranches),
        [data, selectedBranches],
    );

    // Early returns after all hooks
    if (loading) return <div className="bo-loading"><div className="bo-spinner" />Loading branch data...</div>;
    if (error)   return <div className="bo-error">{error}</div>;
    if (!data || !data.branches?.length) return <div className="bo-empty">No branch data available.</div>;

    const branch = aggregated;
    const isSingleSelection = selectedBranches.size === 1;
    const pct = pctOf;
    // Counts in en-US ("1,245"), whatever the browser locale.
    const fmt = (v) => (v ?? 0).toLocaleString('en-US');

    return (
        <div className="bo-section">
            <div className="bo-section-header">
                <h2>Branch Overview</h2>
                <span className="bo-meta">{data.total_units.toLocaleString('en-US')} units · {data.branches.length} branches</span>
            </div>

            <div className="bco-layout">
                {/* Left: branch list */}
                <div className="bco-list">
                    <div className="bco-list-actions">
                        <button
                            type="button"
                            className="bco-list-action"
                            onClick={isAllSelected ? clearAll : selectAll}
                        >
                            {isAllSelected ? 'None' : 'Select all'}
                        </button>
                        <span className="bco-list-action-meta">
                            {selectedBranches.size} of {allBranchNames.length}
                        </span>
                    </div>

                    {data.branches.map(b => {
                        const isChecked = selectedBranches.has(b.branch_name);
                        return (
                            <button
                                key={b.branch_name}
                                type="button"
                                role="checkbox"
                                aria-checked={isChecked}
                                className={`bco-list-item ${isChecked ? 'bco-list-item-active' : ''}`}
                                onClick={() => toggleBranch(b.branch_name)}
                            >
                                <div className="bco-list-row">
                                    <span
                                        className={`bco-list-check ${isChecked ? 'checked' : ''}`}
                                        aria-hidden="true"
                                    >
                                        {isChecked ? '✓' : ''}
                                    </span>
                                    <span className="bco-list-name">{b.branch_name}</span>
                                    <span className="bco-list-count">{fmt(b.total)} units</span>
                                </div>
                                {b.total_value != null && (
                                    <span className="bco-list-value">${(b.total_value / 1_000_000).toFixed(1)}M</span>
                                )}
                                <div className="bco-mini-bar">
                                    {b.fresh    > 0 && <div className="bco-mini-seg" style={{ width: `${(b.fresh/b.total)*100}%`,    background: '#15803d' }} />}
                                    {b.normal   > 0 && <div className="bco-mini-seg" style={{ width: `${(b.normal/b.total)*100}%`,   background: '#1d4ed8' }} />}
                                    {b.slow     > 0 && <div className="bco-mini-seg" style={{ width: `${(b.slow/b.total)*100}%`,     background: '#b45309' }} />}
                                    {b.critical > 0 && <div className="bco-mini-seg" style={{ width: `${(b.critical/b.total)*100}%`, background: '#b91c1c' }} />}
                                </div>
                                <div className="bco-mini-bar">
                                    {b.hot         > 0 && <div className="bco-mini-seg" style={{ width: `${(b.hot/b.total)*100}%`,         background: '#b91c1c' }} />}
                                    {b.warm        > 0 && <div className="bco-mini-seg" style={{ width: `${(b.warm/b.total)*100}%`,        background: '#b45309' }} />}
                                    {b.cold        > 0 && <div className="bco-mini-seg" style={{ width: `${(b.cold/b.total)*100}%`,        background: '#1d4ed8' }} />}
                                    {b.no_interest > 0 && <div className="bco-mini-seg" style={{ width: `${(b.no_interest/b.total)*100}%`, background: '#94a3b8' }} />}
                                </div>
                                {(b.risk_high + b.risk_medium + b.risk_low) > 0 && (() => {
                                    const scored = b.risk_high + b.risk_medium + b.risk_low;
                                    return (
                                        <div className="bco-mini-bar">
                                            {b.risk_high   > 0 && <div className="bco-mini-seg" style={{ width: `${(b.risk_high/scored)*100}%`,   background: '#b91c1c' }} />}
                                            {b.risk_medium > 0 && <div className="bco-mini-seg" style={{ width: `${(b.risk_medium/scored)*100}%`, background: '#b45309' }} />}
                                            {b.risk_low    > 0 && <div className="bco-mini-seg" style={{ width: `${(b.risk_low/scored)*100}%`,    background: '#15803d' }} />}
                                        </div>
                                    );
                                })()}
                            </button>
                        );
                    })}
                </div>

                {/* Right: detail panel */}
                <div className="bco-detail">
                    {!branch || selectedBranches.size === 0 ? (
                        <div className="bo-empty">Select one or more branches above to see a summary.</div>
                    ) : (
                        <>
                            <div className="bco-detail-header">
                                <h3 className="bco-detail-title">{branch.displayTitle || branch.branch_name}</h3>
                                <span className="bco-detail-total">{branch.total.toLocaleString('en-US')} units</span>
                            </div>

                            {/* MachineFinder strip */}
                            {(branch.total_listings != null || branch.avg_price != null) && (
                                <div className="bco-mf-strip">
                                    {branch.total_listings != null && (
                                        <div className="bco-mf-stat">
                                            <span className="bco-mf-label">Listings</span>
                                            <span className="bco-mf-value">{fmt(branch.total_listings)}</span>
                                        </div>
                                    )}
                                    {branch.avg_price != null && (
                                        <div className="bco-mf-stat">
                                            <span className="bco-mf-label">Avg Price</span>
                                            <span className="bco-mf-value bco-mf-price">${Math.round(branch.avg_price).toLocaleString('en-US')}</span>
                                        </div>
                                    )}
                                    {branch.avg_days_listed != null && (
                                        <div className="bco-mf-stat">
                                            <span className="bco-mf-label">Avg Listed</span>
                                            <span className="bco-mf-value">{Math.round(branch.avg_days_listed)}d</span>
                                        </div>
                                    )}
                                    {branch.max_days_listed != null && (
                                        <div className="bco-mf-stat">
                                            <span className="bco-mf-label">Max Listed</span>
                                            <span className={`bco-mf-value ${branch.max_days_listed >= 180 ? 'bco-mf-critical' : ''}`}>{branch.max_days_listed}d</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Aging ── */}
                            <div className="bco-section-title">Aging</div>
                            <div className="bco-full-bar">
                                {branch.fresh    > 0 && <div className="bco-full-seg" style={{ width: `${(branch.fresh/branch.total)*100}%`,    background: '#15803d' }} />}
                                {branch.normal   > 0 && <div className="bco-full-seg" style={{ width: `${(branch.normal/branch.total)*100}%`,   background: '#1d4ed8' }} />}
                                {branch.slow     > 0 && <div className="bco-full-seg" style={{ width: `${(branch.slow/branch.total)*100}%`,     background: '#b45309' }} />}
                                {branch.critical > 0 && <div className="bco-full-seg" style={{ width: `${(branch.critical/branch.total)*100}%`, background: '#b91c1c' }} />}
                            </div>
                            <div className="bco-bucket-grid">
                                <div className="bco-bucket-card bco-bucket-fresh">
                                    <span className="bco-bucket-count">{fmt(branch.fresh)}</span>
                                    <span className="bco-bucket-label">{bucketLabel(1)}</span>
                                    <span className="bco-bucket-pct">{pct(branch.fresh, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-normal">
                                    <span className="bco-bucket-count">{fmt(branch.normal)}</span>
                                    <span className="bco-bucket-label">{bucketLabel(2)}</span>
                                    <span className="bco-bucket-pct">{pct(branch.normal, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-slow">
                                    <span className="bco-bucket-count">{fmt(branch.slow)}</span>
                                    <span className="bco-bucket-label">{bucketLabel(3)}</span>
                                    <span className="bco-bucket-pct">{pct(branch.slow, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-critical">
                                    <span className="bco-bucket-count">{fmt(branch.critical)}</span>
                                    <span className="bco-bucket-label">{bucketLabel(4)}</span>
                                    <span className="bco-bucket-pct">{pct(branch.critical, branch.total)}%</span>
                                </div>
                            </div>
                            <div className="bco-detail-stats">
                                <span className="bco-stat-pill">Avg {Math.round(branch.avg_days_in_stock)}d in stock</span>
                                <span className={`bco-stat-pill ${branch.critical_pct >= 30 ? 'bco-stat-critical' : ''}`}>
                                    {branch.critical_pct.toFixed(0)}% critical
                                </span>
                            </div>

                            {/* ── Buyer Interest ── */}
                            <div className="bco-section-title bco-section-title-mt">Buyer Interest</div>
                            <div className="bco-full-bar">
                                {branch.hot         > 0 && <div className="bco-full-seg" style={{ width: `${(branch.hot/branch.total)*100}%`,         background: '#b91c1c' }} />}
                                {branch.warm        > 0 && <div className="bco-full-seg" style={{ width: `${(branch.warm/branch.total)*100}%`,        background: '#b45309' }} />}
                                {branch.cold        > 0 && <div className="bco-full-seg" style={{ width: `${(branch.cold/branch.total)*100}%`,        background: '#1d4ed8' }} />}
                                {branch.no_interest > 0 && <div className="bco-full-seg" style={{ width: `${(branch.no_interest/branch.total)*100}%`, background: '#94a3b8' }} />}
                            </div>
                            <div className="bco-bucket-grid">
                                <div className="bco-bucket-card bco-bucket-hot">
                                    <span className="bco-bucket-count">{fmt(branch.hot)}</span>
                                    <span className="bco-bucket-label">Hot</span>
                                    <span className="bco-bucket-pct">{pct(branch.hot, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-warm">
                                    <span className="bco-bucket-count">{fmt(branch.warm)}</span>
                                    <span className="bco-bucket-label">Warm</span>
                                    <span className="bco-bucket-pct">{pct(branch.warm, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-cold">
                                    <span className="bco-bucket-count">{fmt(branch.cold)}</span>
                                    <span className="bco-bucket-label">Cold</span>
                                    <span className="bco-bucket-pct">{pct(branch.cold, branch.total)}%</span>
                                </div>
                                <div className="bco-bucket-card bco-bucket-no-interest">
                                    <span className="bco-bucket-count">{fmt(branch.no_interest)}</span>
                                    <span className="bco-bucket-label">No Interest</span>
                                    <span className="bco-bucket-pct">{pct(branch.no_interest, branch.total)}%</span>
                                </div>
                            </div>
                            <div className="bco-detail-stats">
                                <span className={`bco-stat-pill ${branch.active_pct > 0 ? 'bco-stat-active' : ''}`}>
                                    {branch.active_pct.toFixed(0)}% active
                                </span>
                                <span className="bco-stat-pill">{fmt(branch.total_quoted)} of {fmt(branch.total)} quoted</span>
                            </div>

                            {/* ── Sell-Speed Risk ── */}
                            {(branch.risk_high + branch.risk_medium + branch.risk_low) > 0 && (() => {
                                const scored = branch.risk_high + branch.risk_medium + branch.risk_low;
                                return (
                                    <>
                                        <div className="bco-section-title bco-section-title-mt">Sell-Speed Risk</div>
                                        <div className="bco-full-bar">
                                            {branch.risk_high   > 0 && <div className="bco-full-seg" style={{ width: `${(branch.risk_high/scored)*100}%`,   background: '#b91c1c' }} />}
                                            {branch.risk_medium > 0 && <div className="bco-full-seg" style={{ width: `${(branch.risk_medium/scored)*100}%`, background: '#b45309' }} />}
                                            {branch.risk_low    > 0 && <div className="bco-full-seg" style={{ width: `${(branch.risk_low/scored)*100}%`,    background: '#15803d' }} />}
                                        </div>
                                        <div className="bco-bucket-grid">
                                            <div className="bco-bucket-card" style={{ borderLeftColor: '#b91c1c' }}>
                                                <span className="bco-bucket-count" style={{ color: '#b91c1c' }}>{fmt(branch.risk_high)}</span>
                                                <span className="bco-bucket-label">High Risk</span>
                                                <span className="bco-bucket-pct">{pct(branch.risk_high, scored)}%</span>
                                            </div>
                                            <div className="bco-bucket-card" style={{ borderLeftColor: '#b45309' }}>
                                                <span className="bco-bucket-count" style={{ color: '#b45309' }}>{fmt(branch.risk_medium)}</span>
                                                <span className="bco-bucket-label">Medium Risk</span>
                                                <span className="bco-bucket-pct">{pct(branch.risk_medium, scored)}%</span>
                                            </div>
                                            <div className="bco-bucket-card" style={{ borderLeftColor: '#15803d' }}>
                                                <span className="bco-bucket-count" style={{ color: '#15803d' }}>{fmt(branch.risk_low)}</span>
                                                <span className="bco-bucket-label">Low Risk</span>
                                                <span className="bco-bucket-pct">{pct(branch.risk_low, scored)}%</span>
                                            </div>
                                        </div>
                                        <div className="bco-detail-stats">
                                            <span className={`bco-stat-pill ${branch.pct_high_risk >= 30 ? 'bco-stat-critical' : ''}`}>
                                                {branch.pct_high_risk.toFixed(0)}% high-risk
                                            </span>
                                            <span className="bco-stat-pill">{fmt(scored)} of {fmt(branch.total)} scored</span>
                                        </div>
                                    </>
                                );
                            })()}

                            {/* ── Category Breakdown ── */}
                            {categoryData.length > 0 && (
                                <>
                                    <div className="bco-section-title bco-section-title-mt">Category Breakdown</div>
                                    <div className="bco-category-list">
                                        {categoryData.map(cat => (
                                            <div key={cat.name} className="bco-cat-row">
                                                <span className="bco-cat-name">{cat.name}</span>
                                                <div className="bco-cat-bar-wrap">
                                                    <div className="bco-cat-bar" style={{ width: `${cat.pct}%`, background: cat.avgDays > aging.criticalDays ? '#b45309' : '#6f8f5c' }} />
                                                </div>
                                                <span className="bco-cat-count">{fmt(cat.count)}</span>
                                                <span className="bco-cat-days" style={{ color: DAYS_COLORS[daysInStockSeverity(cat.avgDays, aging)] }}>
                                                    {cat.avgDays}d avg
                                                </span>
                                                {cat.critical > 0 && (
                                                    <span className="bco-cat-critical">{cat.critical} crit</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* ── Anomaly Signals ── */}
                            {verdictData.length > 0 && (
                                <>
                                    <div className="bco-section-title bco-section-title-mt">Anomaly Signals</div>
                                    <div className="bco-verdict-grid">
                                        {verdictData.map(v => {
                                            const s = VERDICT_STYLE[v.verdict];
                                            if (!s) return null;
                                            return (
                                                <div key={v.verdict} className="bco-verdict-pill" style={{ background: s.bg, color: s.color }}>
                                                    <span className="bco-verdict-count">{fmt(v.count)}</span>
                                                    <span className="bco-verdict-name">{verdictLabel(v.verdict)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* ── Sell Speed Distribution ── */}
                            {speedData && (
                                <>
                                    <div className="bco-section-title bco-section-title-mt">AI Sell Speed</div>
                                    <div className="bco-speed-bar">
                                        {speedData.fast     > 0 && <div className="bco-speed-seg" style={{ width: `${(speedData.fast/speedData.total)*100}%`,     background: '#15803d' }} />}
                                        {speedData.normal   > 0 && <div className="bco-speed-seg" style={{ width: `${(speedData.normal/speedData.total)*100}%`,   background: '#1d4ed8' }} />}
                                        {speedData.slow     > 0 && <div className="bco-speed-seg" style={{ width: `${(speedData.slow/speedData.total)*100}%`,     background: '#b45309' }} />}
                                        {speedData.verySlow > 0 && <div className="bco-speed-seg" style={{ width: `${(speedData.verySlow/speedData.total)*100}%`, background: '#b91c1c' }} />}
                                    </div>
                                    <div className="bco-speed-grid">
                                        <div className="bco-speed-card" style={{ borderLeftColor: '#15803d' }}>
                                            <span className="bco-speed-count" style={{ color: '#15803d' }}>{fmt(speedData.fast)}</span>
                                            <span className="bco-speed-label">Fast &lt;45d</span>
                                            <span className="bco-speed-pct">{pct(speedData.fast, speedData.total)}%</span>
                                        </div>
                                        <div className="bco-speed-card" style={{ borderLeftColor: '#1d4ed8' }}>
                                            <span className="bco-speed-count" style={{ color: '#1d4ed8' }}>{fmt(speedData.normal)}</span>
                                            <span className="bco-speed-label">Normal 45–90d</span>
                                            <span className="bco-speed-pct">{pct(speedData.normal, speedData.total)}%</span>
                                        </div>
                                        <div className="bco-speed-card" style={{ borderLeftColor: '#b45309' }}>
                                            <span className="bco-speed-count" style={{ color: '#b45309' }}>{fmt(speedData.slow)}</span>
                                            <span className="bco-speed-label">Slow 90–180d</span>
                                            <span className="bco-speed-pct">{pct(speedData.slow, speedData.total)}%</span>
                                        </div>
                                        <div className="bco-speed-card" style={{ borderLeftColor: '#b91c1c' }}>
                                            <span className="bco-speed-count" style={{ color: '#b91c1c' }}>{fmt(speedData.verySlow)}</span>
                                            <span className="bco-speed-label">Very Slow 180+d</span>
                                            <span className="bco-speed-pct">{pct(speedData.verySlow, speedData.total)}%</span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ── Price Alignment vs AI ── */}
                            {priceRealization && (
                                <>
                                    <div className="bco-section-title bco-section-title-mt">Price Alignment vs AI</div>
                                    <div className="bco-pa-wrap">
                                        <div className="bco-pa-main">
                                            <span className="bco-pa-ratio" style={{
                                                color: ALIGN_COLORS[alignmentTone(priceRealization.avg, gapThresholds)],
                                            }}>
                                                {priceRealization.avg}%
                                            </span>
                                            <span className="bco-pa-label">Avg Ask / AI Price</span>
                                        </div>
                                        <div className="bco-pa-breakdown">
                                            <div className="bco-pa-item bco-pa-over">
                                                <span className="bco-pa-num">{fmt(priceRealization.overpriced)}</span>
                                                <span className="bco-pa-desc">Overpriced {bands.over}</span>
                                            </div>
                                            <div className="bco-pa-item bco-pa-aligned">
                                                <span className="bco-pa-num">{fmt(priceRealization.aligned)}</span>
                                                <span className="bco-pa-desc">Aligned {bands.aligned}</span>
                                            </div>
                                            <div className="bco-pa-item bco-pa-below">
                                                <span className="bco-pa-num">{fmt(priceRealization.below)}</span>
                                                <span className="bco-pa-desc">Below {bands.below}</span>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ── AI value by location — follows the branch selection;
                                 all units go in for the Dealership reference row ── */}
                            <BranchAiValueTable
                                units={invUnits}
                                loading={inventoryLoading}
                                selectedBranches={selectedBranches}
                                allSelected={isAllSelected}
                            />

                            {branch.total_value != null && (
                                <div className="bco-total-value">
                                    Total listing value
                                    <strong>${Math.round(branch.total_value).toLocaleString('en-US')}</strong>
                                </div>
                            )}

                            {isSingleSelection && (
                                <button
                                    className="bco-nav-btn"
                                    onClick={() => navigate(`/?branch=${encodeURIComponent(branch.branch_name)}`)}
                                >
                                    View {branch.branch_name} inventory →
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}


BranchCombinedView.propTypes = {
    data: PropTypes.shape({
        branches: PropTypes.array,
        total_units: PropTypes.number,
    }),
    loading: PropTypes.bool,
    error: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    invByBranch: PropTypes.object.isRequired,
    invUnits: PropTypes.arrayOf(PropTypes.object),
    inventoryLoading: PropTypes.bool,
};

BranchCombinedView.defaultProps = {
    data: null,
    loading: false,
    error: null,
    invUnits: [],
    inventoryLoading: false,
};
